import { CaretDown } from '@phosphor-icons/react'
import React, { useEffect, useState } from 'react'
import { leagueEventStore } from 'state/league/leagueEvent'
import { ActivitiesScheduleType, SuppliesType } from 'types/event/events'
import {
  AdditionalFeeResp,
  EstimatedActivity,
  PriceRange,
} from 'types/event/eventStep3'
import { LeagueFinalResp } from 'types/league/league'

interface EstimatedActivitySummaryProps {
  data: LeagueFinalResp
}

const EstimatedActivitySummary: React.FC<EstimatedActivitySummaryProps> = ({
  data,
}) => {
  const { estimateCostPayload, setEstimateCostPayload } = leagueEventStore(
    store => ({
      estimateCostPayload: store.estimateCostPayload,
      setEstimateCostPayload: store.setEstimateCostPayload,
    }),
  )
  const [openActivity, setOpenActivity] = useState<any>(0)
  const [estimatedActivityPrice, setEstimatedActivityPrice] = useState<
    EstimatedActivity[]
  >([])

  const calculateActivityPrice = (
    activity: ActivitiesScheduleType,
  ): EstimatedActivity => {
    let activityPrice = 0
    let activityMinPrice = 0
    let activityMaxPrice = 0

    activity.supplies.forEach((supply: SuppliesType) => {
      const { minGuestsRequired = 1, maxGuestsAllowed = 1 } = data as any

      if (supply.price) {
        if (supply.type === 'reusable') {
          activityPrice += supply.price
        } else if (supply.type === 'consumable') {
          activityMinPrice += supply.price * (minGuestsRequired || 1)
          activityMaxPrice += supply.price * (maxGuestsAllowed || 1)
        }
      } else {
        const { min, max } = supply.priceRange
        if (supply.type === 'reusable') {
          activityMinPrice += min + activityPrice
          activityMaxPrice += max + activityPrice
        } else if (supply.type === 'consumable') {
          activityMinPrice += min * (minGuestsRequired || 1) + activityPrice
          activityMaxPrice += max * (maxGuestsAllowed || 1) + activityPrice
        }
      }
    })

    return {
      name: activity.name,
      price:
        activityMinPrice === 0
          ? activityPrice
          : { min: activityMinPrice, max: activityMaxPrice },
      supplies: activity.supplies || [],
    }
  }

  const estimatedTotalPrice = (): PriceRange => {
    let min = 0
    let max = 0

    min =
      estimateCostPayload.totalStaffSalary.min +
      (typeof estimateCostPayload.venuePrice === 'number'
        ? estimateCostPayload.venuePrice
        : estimateCostPayload.venuePrice.min)
    max =
      estimateCostPayload.totalStaffSalary.max +
      (typeof estimateCostPayload.venuePrice === 'number'
        ? estimateCostPayload.venuePrice
        : estimateCostPayload.venuePrice.max)

    estimatedActivityPrice.forEach(activity => {
      if (typeof activity.price === 'number') {
        min += activity.price
        max += activity.price
      } else {
        min += activity.price.min
        max += activity.price.max
      }
    })

    if ((data as any)?.additionalFee.length > 0) {
      ;(data as any)?.additionalFee.forEach((fee: AdditionalFeeResp) => {
        if (fee.price) {
          min += fee.price
          max += fee.price
        } else if (fee.priceRange) {
          min += fee.priceRange.min
          max += fee.priceRange.max
        }
      })
    }

    return {
      min,
      max,
    }
  }

  useEffect(() => {
    setEstimateCostPayload({
      estimatedTotalPrice: estimatedTotalPrice(),
    })
  }, [
    estimatedActivityPrice,
    estimateCostPayload.totalStaffSalary,
    (data as any)?.additionalFee,
  ])

  return (
    <div>
      {estimatedActivityPrice.length > 0 &&
        estimatedActivityPrice.map((activity, index) => {
          return (
            <div key={index}>
              <div className="flex align-center justify-between gap-20px mt-10px">
                <div className="flex align-center gap-5px">
                  <p>{activity.name}</p>
                  {activity.supplies.length > 0 && (
                    <CaretDown
                      size={14}
                      color="#000"
                      className="pointer"
                      onClick={() =>
                        setOpenActivity(prev => (prev === index ? 0 : index))
                      }
                    />
                  )}
                </div>
                <p>{`$${
                  typeof activity.price === 'number'
                    ? activity.price.toFixed(2)
                    : `${activity.price.min.toFixed(
                        2,
                      )} ~ $${activity.price.max.toFixed(2)}`
                }`}</p>
              </div>

              {activity.supplies.length > 0 &&
                activity.supplies.map((supply: SuppliesType) => {
                  return (
                    openActivity === index && (
                      <div className="flex justify-between align-center gap-5px pl-15px">
                        <p className="text-gray">{supply.name}</p>
                        <p className="text-gray">{`${
                          typeof supply.price === 'number'
                            ? `$${supply.price?.toFixed(2)}`
                            : `$${supply.priceRange.min.toFixed(
                                2,
                              )} ~ $${supply.priceRange.max.toFixed(2)}`
                        }`}</p>
                      </div>
                    )
                  )
                })}
            </div>
          )
        })}
    </div>
  )
}

export default EstimatedActivitySummary
